/**
 * @author Hiroki Moto
 * @description https://marvelapp.com/43fgc52/screen/54135210
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { clone, equals, findIndex, isNil, length, propEq, remove } from 'ramda';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { toast } from 'react-toastify';
import isEmpty from 'ramda/src/isEmpty';
import Accordion from '@components/@core/Accordion';
import Button from '@components/@core/AnimatedButton';
import Dropdown from '@components/@core/Dropdown';
import EditDropdown from '@components/EditDropdown';
import Filter from '@components/@core/Filter';
import Input from '@components/@core/Input';
import { LibraryToDoHabitScheduleDetailMenu } from '~/services/config';
import HabitListItem from './HabitListItem';

class Edit extends Component {
    state = {
        id: this.props.data.id,
        title: this.props.data.name,
        description: this.props.data.data.description,
        selected: {
            author: [this.props.data.author_id],
            department: this.props.data.data.department || [],
            competency: this.props.data.data.competency || [],
            category: this.props.data.data.category || [],
        },
        habits: this.props.data.children,
        disabled: true,
    };

    static getDerivedStateFromProps(nextProps, prevState) {
        const { data, modalData } = nextProps;
        const habits = data.children || [];
        const { title, description, selected } = prevState;
        const disabled =
            data.name == title &&
            data.data.description == description &&
            equals(selected.author, [data.author_id]) &&
            equals(selected.department, data.data.department || []) &&
            equals(selected.competency, data.data.competency || []) &&
            equals(selected.category, data.data.category || []) &&
            equals(prevState.habits, habits);
        if (modalData?.after?.habits) {
            const _habits = modalData?.after?.habits;
            if (!equals(_habits, prevState.habits)) {
                return { habits: _habits };
            }
        }
        if (!equals(habits, prevState.habits)) {
            return { habits: prevState.habits, disabled };
        } else return { disabled };
    }

    handleAddHabits = () => {
        const { habits } = this.state;
        this.props.onModal({
            type: 'Attach Library',
            data: { before: { show: ['habits'], modules: [], selected: habits }, after: habits },
            callBack: {
                onAttach: (habits) => this.setState({ habits: habits.templates }),
                onAdd: () => this.props.onModal({ type: 'Add New Habit', data: { after: { habits } } }),
            },
        });
    };

    handleSelectTags = (key) => (tags) => {
        const { selected } = this.state;
        if (!equals(selected[key], tags)) {
            selected[key] = tags;
            this.setState({ selected });
        }
    };

    handleSelectMenu = (habit) => (event) => {
        const { habits } = this.state;
        const index = findIndex(propEq('id', habit.id), habits);
        if (index > -1) {
            let newHabits = clone(habits);
            if (event === 'remove') {
                newHabits = remove(index, 1, newHabits);
            }
            this.setState({ habits: newHabits });
        }
    };

    handleSubmit = (published) => () => {
        const { id, title, description, habits, selected } = this.state;

        if (isEmpty(title) || isEmpty(description)) {
            toast.error(`Title and Description are required.`, {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
            return;
        }

        const _habits = habits.map((e) => ({
            blocked_by: null,
            card_template_id: e.id,
            card_type_id: 17,
            complete_course: 0,
            delay_days: 0,
        }));
        const data = {
            template: {
                id,
                card_type_id: 17,
                name: title,
                author_id: selected.author[0],
                access_type: 1,
                children: habits,
                published,
                data: {
                    description,
                    modules: _habits,
                    author: selected.author,
                    category: selected.category,
                    competency: selected.competency,
                    department: selected.department,
                    type: 'recurring',
                },
            },
        };
        const payload = { type: 'habitslist', data };
        this.props.onUpdate(payload);
    };

    render() {
        const { title, description, selected, habits, disabled } = this.state;
        const { data, authors, departments, competencies, categories, userRole, onSelect } = this.props;
        const { published } = data;

        const daily = [],
            weekly = [],
            monthly = [];
        habits.map((item) => {
            const { schedule_interval } = item.data;
            if (schedule_interval === 'day') daily.push(item);
            if (schedule_interval === 'week') weekly.push(item);
            if (schedule_interval === 'month' || isNil(schedule_interval)) {
                monthly.push(item);
            }
        });

        return (
            <>
                <Filter back="all habit schedules" />
                <div className="lib-todo-habit-schedule" data-cy="editHabitScheduleForm">
                    <div className="habit-schedule-detail">
                        <div className="d-flex jc-between py-2">
                            <p className="dsl-b18 bold mb-0">Edit Habit Schedule</p>
                            <EditDropdown options={LibraryToDoHabitScheduleDetailMenu[userRole]} onChange={onSelect} />
                        </div>
                        <Input
                            className="input-field"
                            title="Title"
                            dataCy="title"
                            type="text"
                            direction="vertical"
                            value={title}
                            placeholder="Type here..."
                            onChange={(title) => this.setState({ title })}
                        />
                        <Input
                            title="Description"
                            dataCy="description"
                            className="input-field input-textarea"
                            type="text"
                            as="textarea"
                            rows="2"
                            direction="vertical"
                            value={description}
                            placeholder="Type here..."
                            onChange={(description) => this.setState({ description })}
                        />
                        <Accordion className="settings-habit-schedule" icon="fas fa-gear">
                            <Row className="mt-1">
                                <Col xs={12} sm={6} className="my-3">
                                    <Dropdown
                                        className="dropdown-field"
                                        title="Author"
                                        direction="vertical"
                                        dataCy="author"
                                        width="fit-content"
                                        getValue={(e) => e.name}
                                        defaultIds={selected.author}
                                        data={authors}
                                        onChange={this.handleSelectTags('author')}
                                    />
                                </Col>
                                <Col xs={12} sm={6} className="my-3">
                                    <Dropdown
                                        multi
                                        className="dropdown-field"
                                        title="Categories"
                                        direction="vertical"
                                        dataCy="categories"
                                        width="fit-content"
                                        placeholder="Select multiple"
                                        getValue={(e) => e.name}
                                        defaultIds={selected.category}
                                        data={categories}
                                        onChange={this.handleSelectTags('category')}
                                    />
                                </Col>
                                <Col xs={12} sm={6} className="my-3">
                                    <Dropdown
                                        multi
                                        className="dropdown-field"
                                        title="Competencies"
                                        direction="vertical"
                                        dataCy="competencies"
                                        width="fit-content"
                                        placeholder="Select multiple"
                                        getValue={(e) => e.name}
                                        defaultIds={selected.competency}
                                        data={competencies}
                                        onChange={this.handleSelectTags('competency')}
                                    />
                                </Col>
                                <Col xs={12} sm={6} className="my-3">
                                    <Dropdown
                                        multi
                                        className="dropdown-field"
                                        title="Departments"
                                        direction="vertical"
                                        dataCy="department"
                                        width="fit-content"
                                        placeholder="Select multiple"
                                        getValue={(e) => e.name}
                                        defaultIds={selected.department}
                                        data={departments}
                                        onChange={this.handleSelectTags('department')}
                                    />
                                </Col>
                            </Row>
                        </Accordion>
                    </div>
                    <div className="detail-list mt-3" data-cy="editHabitIncluded">
                        <p className="dsl-b22 bold pt-3">Habits Included</p>
                        {length(habits) > 0 ? (
                            <>
                                <p className="dsl-b18 text-500">Daily Habits</p>
                                <div data-cy="editHabitScheduleDailyHabit" className={`mb-4 ${length(daily) > 0 ? 'border-bottom' : ''}`}>
                                    {daily.map((habit, index) => (
                                        <HabitListItem
                                            dataCy={`editDailyHabitListItem${index}`}
                                            key={habit.id}
                                            name={habit.name}
                                            description={habit.data.description}
                                            assigned={habit.data.assigned || 0}
                                            editable
                                            onSelect={this.handleSelectMenu(habit)}
                                        />
                                    ))}
                                </div>
                                <p className="dsl-b18 text-500">Weekly Habits</p>
                                <div data-cy="editHabitScheduleWeeklyHabit" className={`mb-4 ${length(weekly) > 0 ? 'border-bottom' : ''}`}>
                                    {weekly.map((habit, index) => (
                                        <HabitListItem
                                            key={habit.id}
                                            name={habit.name}
                                            description={habit.data.description}
                                            assigned={habit.data.assigned || 0}
                                            editable
                                            onSelect={this.handleSelectMenu(habit)}
                                            dataCy={`editWeeklyHabitListItem${index}`}
                                        />
                                    ))}
                                </div>
                                <p className="dsl-b18 text-500">Monthly Habits</p>
                                <div data-cy="editHabitScheduleMonthlyHabit" className={`mb-4 ${length(monthly) > 0 ? 'border-bottom' : ''}`}>
                                    {monthly.map((habit, index) => (
                                        <HabitListItem
                                            key={habit.id}
                                            name={habit.name}
                                            description={habit.data.description}
                                            assigned={habit.data.assigned || 0}
                                            editable
                                            onSelect={this.handleSelectMenu(habit)}
                                            dataCy={`editMonthlyHabitListItem${index}`}
                                        />
                                    ))}
                                </div>
                            </>
                        ) : (
                            <p className="dsl-m12 mb-0" data-cy="noHabitAddedYet">
                                No habits added yet
                            </p>
                        )}
                        <div className="jc-end d-flex px-3">
                            <Button className="px-0" name="+ Attach Habit" type="link" onClick={this.handleAddHabits} />
                        </div>
                        <div className="mt-2 d-flex jc-end px-3">
                            <Button className="btn-save me-3" name="Draft" disabled={disabled && published !== 1} type="low" onClick={this.handleSubmit(0)} />
                            <Button className="btn-save" name="Publish" disabled={disabled && published !== 0} onClick={this.handleSubmit(1)} />
                        </div>
                    </div>
                </div>
            </>
        );
    }
}

Edit.propTypes = {
    editable: PropTypes.bool,
    data: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        data: PropTypes.any,
        author_id: PropTypes.number,
    }),
    tags: PropTypes.array,
    onUpdate: PropTypes.func,
    onModal: PropTypes.func,
};

Edit.defaultProps = {
    editable: false,
    tags: [],
    data: {
        id: 0,
        name: '',
        data: {},
        author_id: 0,
    },
    onUpdate: () => {},
    onModal: () => {},
};

export default Edit;
